import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js"; // Reused component
import Features from "../../../../Components/Features/ThreeColumnPages"; // Reused component
import {
  FaCode,
  FaMobileAlt,
  FaUsers,
  FaHandshake,
  FaAward,
  FaMapMarkerAlt,
  FaChartLine,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

// UX-Focused Web Development in Dubai Page Component
export const UXFocusedWebDevelopmentInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="UX-Focused Web Development in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={
          <Subheading>
            Building Websites with Exceptional User Experiences
          </Subheading>
        }
        heading={
          <Heading>
            Elevate Your Online Presence with{" "}
            <span tw="text-primary-500">
              UX-Focused Web Development in Dubai
            </span>
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we specialize in delivering websites that
            prioritize user experience (UX) to help businesses across **Dubai**
            thrive online. Our team combines cutting-edge technology,
            creativity, and data-driven strategies to craft websites that are
            not only visually stunning but also intuitive and engaging for
            users.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1531403009284-440f080d1e12?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UX design process
          "https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Team collaboration on UX
          "https://images.unsplash.com/photo-1565106430482-8f6e74349ca1?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // User journey mapping
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Get Started Today"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Your Trusted Partner for UX-Focused Web Development in Dubai"
        description="We combine expertise, creativity, and innovation to deliver unmatched value."
        cards={[
          {
            icon: <FaCode color="#ff5464" size={40} />,
            title: "Cutting-Edge Technology",
            description:
              "Build websites using the latest tools and frameworks for optimal performance.",
          },
          {
            icon: <FaMobileAlt color="#ff5464" size={40} />,
            title: "Responsive Design",
            description:
              "Ensure your website looks and performs flawlessly on all devices.",
          },
          {
            icon: <FaMapMarkerAlt color="#ff5464" size={40} />,
            title: "Localized Expertise",
            description:
              "Understand Dubai's unique market trends and diverse demographics.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "User-Centric Approach",
            description:
              "Prioritize usability, accessibility, and engagement for all users.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client-Centric Collaboration",
            description:
              "Work closely with you to ensure alignment with your vision and goals.",
          },
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Proven Track Record",
            description:
              "Delivering exceptional results for Dubai clients since [Year].",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Streamlined Process for UX-Focused Web Development in Dubai"
        description={
          <Description>
            Our Dubai-focused process ensures quality, efficiency, and
            measurable results:
            <ul tw="mt-4 list-disc pl-6">
              <li>
                Discovery & Research: Understand your business, audience, and
                goals.
              </li>
              <li>
                User Analysis: Identify key user personas and their preferences.
              </li>
              <li>
                Design & Prototyping: Create wireframes and interactive
                prototypes for testing.
              </li>
              <li>
                Development: Build responsive, high-performance websites
                tailored to your needs.
              </li>
              <li>
                Testing & Optimization: Test for usability, performance, and
                accessibility.
              </li>
              <li>
                Launch & Support: Deploy your website and provide ongoing
                support.
              </li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1587614382346-4ec70e388b28?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Wireframing process
          "https://images.unsplash.com/photo-1543286386-713bdd548da4?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // User testing session
          "https://images.unsplash.com/photo-1552663651-2e4250e6c7e2?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UX prototype review
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Results That Speak for Themselves"
        description="Here’s how our UX-focused web development services have empowered businesses in Dubai."
        cards={[
          {
            icon: <FaAward color="#ff5464" size={40} />,
            title: "Improved User Engagement",
            description:
              "Helped a local e-commerce platform in Dubai increase user retention by 150% through intuitive design.",
          },
          {
            icon: <FaHandshake color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description:
              "98% client retention rate through consistent excellence and measurable results.",
          },
          {
            icon: <FaChartLine color="#ff5464" size={40} />,
            title: "Performance Boost",
            description:
              "Achieved a 200% increase in conversions for a retail app using optimized UX strategies.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready to Transform Your Website?</Subheading>}
        heading="Let’s Create Impactful Solutions Together in Dubai"
        description={
          <Description>
            Partner with Dubai’s leading UX-focused web development experts to
            unlock your business’s full potential. Whether you’re launching a
            new website or revamping an existing one, we’re here to help.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1551033406-611cf9a28f67?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // UX team celebrating project completion
          "https://images.unsplash.com/photo-1470790376778-a9fbc86d70e2?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Dubai business district
          "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?ixlib=rb-4.0.3&auto=format&fit=crop&q=80", // Client collaboration meeting
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Today"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
